// App.jsx
import React, { useState, useEffect } from "react";
import TicketList from "./components/TicketList";
import { useNavigate, useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";

function App() {
  const navigate = useNavigate();
  const AppService = useAppServices();
  const { locationId } = useParams();
  const [currentPage, setCurrentPage] = useState("list");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    closed: 0,
  });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const total = tickets.length;
    const open = tickets.filter((t) => t.status === "Open").length;
    const inProgress = tickets.filter((t) => t.status === "In Progress").length;
    const closed = tickets.filter((t) => t.status === "Closed").length;

    setStats({ total, open, inProgress, closed });
  }, [tickets]);

  const getTickets = async () => {
    try {
      const { response } = await AppService.support.get_tickets({
        query: `locationId=${locationId}`,
      });
      if (response) {
        setTickets(response.data);
      } else {
        setTickets([]);
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTickets();
  }, []);

  const handleViewTicket = (ticket) => {
    navigate(`./ticket_detail/${ticket._id}`);
  };

  const handleDeleteTicket = (ticketId) => {
    if (window.confirm("Are you sure you want to delete this ticket?")) {
      setTickets(tickets.filter((ticket) => ticket.id !== ticketId));
      if (selectedTicket && selectedTicket.id === ticketId) {
        setCurrentPage("list");
      }
    }
  };

  const getStatusCount = (status) => {
    return tickets.filter((t) => t.status === status).length;
  };

  const getPriorityCount = (priority) => {
    return tickets.filter((t) => t.priority === priority).length;
  };

  const closedRate =
    stats.total > 0 ? Math.round((stats.closed / stats.total) * 100) : 0;

  return (
    <div className="st-page">
      {/* Header */}
      <header className="st-header">
        <div className="container-fluid st-header-inner d-flex align-items-center justify-content-between flex-wrap gap-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <span className="st-header-icon d-inline-flex align-items-center justify-content-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6" />
                <path d="M22 10a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                <path d="M2 14h4" />
                <path d="M18 14h4" />
              </svg>
            </span>
            <div>
              <h3 className="st-title mb-1" style={{ fontSize: "1.375rem", fontWeight: 700, letterSpacing: "-0.015em", lineHeight: 1.15, color: "#0f172a" }}>
                Support tickets
              </h3>
              <p className="st-subtitle mb-0">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1" style={{ verticalAlign: "-2px" }}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                View and manage your support tickets
              </p>
            </div>
          </div>
          <button
            type="button"
            className="st-btn st-btn--primary"
            onClick={() => navigate("./create_ticket")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="me-1">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New ticket
          </button>
        </div>
      </header>

      <main className="container-fluid py-4 px-4">
        {loading ? (
          <div className="st-loader d-flex flex-column align-items-center justify-content-center">
            <span className="st-spinner" />
            <div className="st-loader-title">Loading tickets…</div>
            <div className="st-loader-meta">
              Fetching your support tickets
            </div>
          </div>
        ) : (
          <>
        {currentPage === "list" && (
          <div className="row mb-4 g-3">
            <div className="col-xl-3 col-md-6">
              <div className="st-stat">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="st-stat-label">Total tickets</div>
                    <div className="st-stat-value">{stats.total}</div>
                    <div className="st-stat-trend st-stat-trend--positive">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <line x1="12" y1="19" x2="12" y2="5" />
                        <polyline points="5 12 12 5 19 12" />
                      </svg>
                      12% from last month
                    </div>
                  </div>
                  <span className="st-stat-icon st-stat-icon--neutral">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 10v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6" />
                      <path d="M22 10a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className="st-stat">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="st-stat-label">Open</div>
                    <div className="st-stat-value" style={{ color: "#2563eb" }}>{stats.open}</div>
                    <div className="st-stat-trend" style={{ color: "#2563eb" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {stats.open > 0 ? "Needs attention" : "All clear"}
                    </div>
                  </div>
                  <span className="st-stat-icon st-stat-icon--blue">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className="st-stat">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="st-stat-label">In progress</div>
                    <div className="st-stat-value" style={{ color: "#d97706" }}>{stats.inProgress}</div>
                    <div className="st-stat-trend" style={{ color: "#d97706" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <polyline points="1 4 1 10 7 10" />
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                      </svg>
                      Active resolution
                    </div>
                  </div>
                  <span className="st-stat-icon st-stat-icon--amber">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10" />
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-md-6">
              <div className="st-stat">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="st-stat-label">Closed</div>
                    <div className="st-stat-value" style={{ color: "#16a34a" }}>{stats.closed}</div>
                    <div className="st-stat-trend" style={{ color: "#16a34a" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {closedRate}% resolution rate
                    </div>
                  </div>
                  <span className="st-stat-icon st-stat-icon--green">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <TicketList
              tickets={tickets}
              onViewTicket={handleViewTicket}
              onEditTicket={(ticket) => {
                setSelectedTicket(ticket);
                setCurrentPage("detail");
              }}
              onDeleteTicket={handleDeleteTicket}
            />
          </div>
        </div>
          </>
        )}
      </main>

      <style>{`
        .st-page {
          font-family: 'Inter', sans-serif;
          background: #fafafa;
          min-height: 100vh;
        }

        /* Loader */
        .st-loader {
          min-height: 50vh;
          gap: 14px;
          text-align: center;
        }
        .st-spinner {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 4px solid #e5e7eb;
          border-top-color: #4f46e5;
          animation: st-spin 0.8s linear infinite;
        }
        @keyframes st-spin { to { transform: rotate(360deg); } }
        .st-loader-title {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #0f172a;
        }
        .st-loader-meta {
          font-size: 0.8125rem;
          color: #6b7280;
          margin-top: -8px;
        }

        /* Header */
        .st-header {
          background: #fff;
          border-bottom: 1px solid #ececec;
          padding: 18px 0;
        }
        .st-header-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
          color: #4f46e5;
          flex-shrink: 0;
          border: 1px solid #e0e7ff;
          box-shadow: 0 1px 2px rgba(79, 70, 229, 0.06);
        }
        .st-subtitle {
          font-size: 0.8125rem;
          color: #6b7280;
          line-height: 1.5;
        }

        /* Stat cards */
        .st-stat {
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 12px;
          padding: 18px 20px;
          transition: border-color 0.15s ease, box-shadow 0.2s ease;
          height: 100%;
        }
        .st-stat:hover {
          border-color: #dcdcdc;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 4px 12px rgba(17, 24, 39, 0.04);
        }
        .st-stat-label {
          font-size: 0.6875rem;
          font-weight: 600;
          color: #64748b;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .st-stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
          margin: 6px 0 8px;
          line-height: 1.1;
        }
        .st-stat-trend {
          display: inline-flex;
          align-items: center;
          font-size: 0.75rem;
          font-weight: 500;
          color: #6b7280;
        }
        .st-stat-trend--positive { color: #16a34a; }
        .st-stat-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .st-stat-icon--neutral { background: #f3f4f6; color: #4b5563; }
        .st-stat-icon--blue { background: #eff6ff; color: #2563eb; }
        .st-stat-icon--amber { background: #fef3c7; color: #d97706; }
        .st-stat-icon--green { background: #f0fdf4; color: #16a34a; }

        /* Buttons */
        .st-btn {
          display: inline-flex;
          align-items: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 9px 16px;
          border-radius: 9px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          text-decoration: none;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .st-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .st-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .st-btn--primary:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          color: #fff;
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .st-btn--primary:active { transform: translateY(0); }
      `}</style>
    </div>
  );
}

export default App;
